// src/store/slices/onboardingSlice.ts

import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

/**
 * Redux state for onboarding form
 *
 * This slice manages multi-step onboarding form state with:
 * - Form data across all steps
 * - Navigation and progress tracking
 * - Validation state per step
 * - LocalStorage persistence
 */

// ============================================================================
// TYPES & INTERFACES
// ============================================================================

export type RiskAppetite =
  | "conservative"
  | "moderate"
  | "aggressive"
  | "very-aggressive";
export type TimeHorizon = "short" | "medium" | "long";
export type ExperienceLevel =
  | "beginner"
  | "intermediate"
  | "advanced"
  | "professional";
export type ReportFrequency = "daily" | "weekly" | "monthly" | "quarterly";
export type ReportLength = "brief" | "medium" | "detailed" | "full-research";
export type EmailFrequency =
  | "immediate"
  | "daily-digest"
  | "weekly-digest"
  | "never";

export interface OnboardingFormData {
  // Step 1: Sector Interests
  sectorInterests: string[];

  // Step 2: Risk & Investment Profile
  riskAppetite: RiskAppetite | "";
  timeHorizon: TimeHorizon | "";
  experienceLevel: ExperienceLevel | "";

  // Step 3: Team/Organization Details
  teamSize: number;
  aum: string;
  companyType: string;

  // Step 4: Report Preferences
  reportFrequency: ReportFrequency | "";
  reportLength: ReportLength | "";

  // Step 5: Geographic Focus
  geographicFocus: string[];

  // Step 6: Communication Preferences
  emailFrequency: EmailFrequency | "";
  notificationTypes: string[];

  // Step 7: Metrics Interests
  preferredMetrics: string[];
  specificIndicators: string[];
  additionalNotes: string;
}

interface OnboardingState {
  // Form Data
  formData: OnboardingFormData;

  // Navigation State
  currentStep: number;
  totalSteps: number;
  completedSteps: number[];

  // Validation State
  stepValidation: Record<number, boolean>;
  errors: Record<string, string>;

  // UI State
  isSubmitting: boolean;
  isComplete: boolean;
  showConfirmation: boolean;

  // Persistence
  lastSaved: string | null; // ISO timestamp
  isDirty: boolean; // Has unsaved changes
}

// ============================================================================
// LOCALSTORAGE UTILITIES
// ============================================================================

const CACHE_KEY = "hermes_onboarding_form";
const FORM_VERSION = "1.0";

interface CachedOnboardingData {
  formData: OnboardingFormData;
  currentStep: number;
  completedSteps: number[];
  savedAt: string;
  version: string;
}

/**
 * Save onboarding state to localStorage
 */
const saveOnboardingToLocal = (state: OnboardingState): void => {
  try {
    const cacheData: CachedOnboardingData = {
      formData: state.formData,
      currentStep: state.currentStep,
      completedSteps: state.completedSteps,
      savedAt: new Date().toISOString(),
      version: FORM_VERSION,
    };
    localStorage.setItem(CACHE_KEY, JSON.stringify(cacheData));
  } catch (error) {
    console.warn("Failed to save onboarding form to localStorage:", error);
  }
};

/**
 * Load onboarding state from localStorage
 */
const loadOnboardingFromLocal = (): CachedOnboardingData | null => {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (!cached) return null;

    const parsed = JSON.parse(cached) as CachedOnboardingData;

    // Check version compatibility
    if (parsed.version !== FORM_VERSION) {
      console.warn("Onboarding form version mismatch, clearing cache");
      localStorage.removeItem(CACHE_KEY);
      return null;
    }

    return parsed;
  } catch (error) {
    console.warn("Failed to load onboarding form from localStorage:", error);
    localStorage.removeItem(CACHE_KEY);
    return null;
  }
};

/**
 * Clear onboarding state from localStorage
 */
export const clearOnboardingLocal = (): void => {
  try {
    localStorage.removeItem(CACHE_KEY);
  } catch (error) {
    console.warn("Failed to clear onboarding form from localStorage:", error);
  }
};

/**
 * Check if there's an incomplete onboarding form in localStorage
 */
export const hasIncompleteOnboarding = (): boolean => {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    return !!cached;
  } catch {
    return false;
  }
};

// ============================================================================
// INITIAL STATE
// ============================================================================

const defaultFormData: OnboardingFormData = {
  // Step 1
  sectorInterests: [],

  // Step 2
  riskAppetite: "",
  timeHorizon: "",
  experienceLevel: "",

  // Step 3
  teamSize: 10,
  aum: "",
  companyType: "",

  // Step 4
  reportFrequency: "",
  reportLength: "",

  // Step 5
  geographicFocus: [],

  // Step 6
  emailFrequency: "",
  notificationTypes: [],

  // Step 7
  preferredMetrics: [],
  specificIndicators: [],
  additionalNotes: "",
};

// Load cached data for initial state
const cachedData = loadOnboardingFromLocal();

const initialState: OnboardingState = {
  formData: cachedData?.formData || defaultFormData,
  currentStep: cachedData?.currentStep || 1,
  totalSteps: 7,
  completedSteps: cachedData?.completedSteps || [],
  stepValidation: {},
  errors: {},
  isSubmitting: false,
  isComplete: false,
  showConfirmation: false,
  lastSaved: cachedData?.savedAt || null,
  isDirty: false,
};

// ============================================================================
// SLICE
// ============================================================================

const onboardingSlice = createSlice({
  name: "onboarding",
  initialState,
  reducers: {
    /**
     * Set current step
     */
    setCurrentStep: (state, action: PayloadAction<number>) => {
      if (action.payload >= 1 && action.payload <= state.totalSteps) {
        state.currentStep = action.payload;
        state.isDirty = true;
      }
    },

    /**
     * Update a single form field
     */
    updateFormField: (
      state,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      action: PayloadAction<{ field: keyof OnboardingFormData; value: any }>
    ) => {
      const { field, value } = action.payload;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (state.formData as any)[field] = value;
      state.isDirty = true;

      // Clear field-specific error
      if (state.errors[field]) {
        delete state.errors[field];
      }
    },

    /**
     * Update multiple fields at once (for a step)
     */
    updateStepData: (
      state,
      action: PayloadAction<Partial<OnboardingFormData>>
    ) => {
      state.formData = {
        ...state.formData,
        ...action.payload,
      };
      state.isDirty = true;
    },

    /**
     * Mark a step as complete
     */
    markStepComplete: (state, action: PayloadAction<number>) => {
      const step = action.payload;
      if (!state.completedSteps.includes(step)) {
        state.completedSteps.push(step);
      }
      state.stepValidation[step] = true;
      state.isDirty = true;
    },

    /**
     * Mark a step as incomplete
     */
    markStepIncomplete: (state, action: PayloadAction<number>) => {
      const step = action.payload;
      state.completedSteps = state.completedSteps.filter((s) => s !== step);
      state.stepValidation[step] = false;
      state.isDirty = true;
    },

    /**
     * Set validation errors
     */
    setErrors: (state, action: PayloadAction<Record<string, string>>) => {
      state.errors = action.payload;
    },

    /**
     * Clear all validation errors
     */
    clearErrors: (state) => {
      state.errors = {};
    },

    /**
     * Clear a specific field error
     */
    clearFieldError: (state, action: PayloadAction<string>) => {
      delete state.errors[action.payload];
    },

    /**
     * Reset form to initial state
     */
    resetForm: (state) => {
      state.formData = defaultFormData;
      state.currentStep = 1;
      state.completedSteps = [];
      state.stepValidation = {};
      state.errors = {};
      state.isSubmitting = false;
      state.isComplete = false;
      state.showConfirmation = false;
      state.lastSaved = null;
      state.isDirty = false;
      clearOnboardingLocal();
    },

    /**
     * Load form data from localStorage
     */
    loadFormData: (state, action: PayloadAction<OnboardingFormData>) => {
      state.formData = action.payload;
      state.isDirty = false;
    },

    /**
     * Set submitting state
     */
    setIsSubmitting: (state, action: PayloadAction<boolean>) => {
      state.isSubmitting = action.payload;
    },

    /**
     * Set complete state
     */
    setIsComplete: (state, action: PayloadAction<boolean>) => {
      state.isComplete = action.payload;
    },

    /**
     * Show confirmation page
     */
    setShowConfirmation: (state, action: PayloadAction<boolean>) => {
      state.showConfirmation = action.payload;
    },

    /**
     * Save current state to localStorage
     */
    saveToLocalStorage: (state) => {
      saveOnboardingToLocal(state);
      state.lastSaved = new Date().toISOString();
      state.isDirty = false;
    },

    /**
     * Go to next step (with validation)
     */
    nextStep: (state) => {
      if (state.currentStep < state.totalSteps) {
        state.currentStep += 1;
        saveOnboardingToLocal(state);
        state.lastSaved = new Date().toISOString();
        state.isDirty = false;
      } else {
        // Last step, show confirmation
        state.showConfirmation = true;
      }
    },

    /**
     * Go to previous step
     */
    previousStep: (state) => {
      if (state.currentStep > 1) {
        state.currentStep -= 1;
        state.showConfirmation = false;
        state.isDirty = true;
      }
    },

    /**
     * Submit form (final action)
     */
    submitForm: (state) => {
      state.isComplete = true;
      state.isSubmitting = false;
      clearOnboardingLocal();
    },
  },
});

// ============================================================================
// EXPORTS
// ============================================================================

export const {
  setCurrentStep,
  updateFormField,
  updateStepData,
  markStepComplete,
  markStepIncomplete,
  setErrors,
  clearErrors,
  clearFieldError,
  resetForm,
  loadFormData,
  setIsSubmitting,
  setIsComplete,
  setShowConfirmation,
  saveToLocalStorage,
  nextStep,
  previousStep,
  submitForm,
} = onboardingSlice.actions;

export default onboardingSlice.reducer;
